"use client";

import { useEffect, useRef, useState } from "react";
import { getMapConfig } from "@/lib/map-config";

const mapImageCache = new Map<string, HTMLImageElement>();

/** Loads a map radar image given a map name. */
export function useMapImage(mapName: string | null) {
  const mapImageRef = useRef<HTMLImageElement | null>(null);
  const [loadedMapName, setLoadedMapName] = useState<string | null>(null);
  const mapImageLoaded = Boolean(mapName && loadedMapName === mapName);

  useEffect(() => {
    let cancelled = false;
    mapImageRef.current = null;
    setLoadedMapName(null);

    if (!mapName) return;

    const mapConfig = getMapConfig(mapName);
    if (!mapConfig?.radarImage) return;

    const cachedImage = mapImageCache.get(mapConfig.radarImage);
    if (cachedImage) {
      mapImageRef.current = cachedImage;
      setLoadedMapName(mapName);
      return;
    }

    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      mapImageCache.set(mapConfig.radarImage!, img);
      mapImageRef.current = img;
      setLoadedMapName(mapName);
    };
    img.onerror = () => {
      if (cancelled) return;
      console.warn(`Map image not found: ${mapConfig.radarImage}`);
    };
    img.src = mapConfig.radarImage;

    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [mapName]);

  return { mapImageRef, mapImageLoaded };
}
